<script setup lang="ts">
import useNotification from '@/composables/notification'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'

import { offerRewardTypeBlockKey } from '@/components/rewards/utils/offerRewardTypeBlockContext'

import RewardModal from '@/components/rewards/RewardModal.vue'

import type { LoyaltyReward } from '@/types/types'

defineProps<{
  rewardId?: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:reward', reward: LoyaltyReward): void
}>()

const { t } = useI18n()
const notification = useNotification()

const { currentReward, rewardTypeIdsBySlug, isOfferFormTouched, hasOtherUsageThanOffer, handleRewardUpdate } =
  useRewardForm(emit)

provide(offerRewardTypeBlockKey, {
  formTouched: isOfferFormTouched,
  rewardTypeIdsBySlug,
  accessConditions: {}
})

const showRewardConfirmModal = ref(false)
const pendingRewardToSave = ref<LoyaltyReward | null>(null)

const usageKeyMap: Record<string, string> = {
  loyalty: 'pages.reward.library.usage.loyalty',
  marketing_flow: 'pages.reward.library.usage.marketing',
  autopilot: 'pages.reward.library.usage.autopilot',
  contest: 'pages.reward.library.usage.contest',
  campaign_sms: 'pages.reward.library.usage.campaignSms',
  campaign_push: 'pages.reward.library.usage.campaignPush'
}

const nonOfferUsageModules = computed(() =>
  (currentReward.value?.usages ?? [])
    .filter((u) => u !== 'offer')
    .map((u) => (usageKeyMap[u] ? t(usageKeyMap[u]) : u))
    .join(', ')
)

const handleSaveWithConfirmation = async (reward: LoyaltyReward) => {
  if (hasOtherUsageThanOffer.value) {
    pendingRewardToSave.value = reward
    showRewardConfirmModal.value = true
    return
  }
  await handleRewardUpdate(reward)
}

const handleConfirmModify = async () => {
  if (!pendingRewardToSave.value) return
  showRewardConfirmModal.value = false
  await handleRewardUpdate(pendingRewardToSave.value)
  pendingRewardToSave.value = null
}
</script>

<template>
  <RewardModal
    :reward-id="rewardId"
    usage="offer"
    :is-external-form-modified="isOfferFormTouched"
    :save-reward="handleSaveWithConfirmation"
    @close="emit('close')"
    @save-error="() => notification.create('error', { text: t('pages.reward.editReward.error') })"
  >
    <template #additional-summary="{ hasChanges }">
      <BaseTip
        v-if="hasOtherUsageThanOffer && hasChanges"
        severity="info"
        icon-name="IconoirInfoCircle"
        :text="t('pages.offer.reward.sharedRewardNotice')"
      />
    </template>
  </RewardModal>

  <BaseModal :is-open="showRewardConfirmModal" size="lg" :show-footer="false" @close="showRewardConfirmModal = false">
    <div class="flex w-full flex-row justify-center">
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
        <BaseIcon name="IconoirWarningTriangle" class="h-6 w-6 text-yellow-600" />
      </div>
    </div>
    <p class="mt-5 text-center font-semibold">
      {{ t('pages.offer.reward.confirmModifyTitle') }}
    </p>
    <p class="mb-5 mt-2 text-center text-sm text-foreground-secondary">
      {{ t('pages.offer.reward.confirmModifyBody', { modules: nonOfferUsageModules }) }}
    </p>
    <div class="flex w-full flex-row justify-center gap-3">
      <Button variant="outline" class="flex-1" @click="showRewardConfirmModal = false">
        {{ t('global.words.cancel') }}
      </Button>
      <Button variant="default-primary" class="flex-1" @click="handleConfirmModify">
        {{ t('global.words.confirm') }}
      </Button>
    </div>
  </BaseModal>
</template>
